/** Rolling-straddle SVG canvas: the ATM CE+PE premium line with optional
 * VWAP / CE / PE companions on the RIGHT (premium) axis and spot on the
 * LEFT axis — StockMock's layout. Data prep (resample/zoom) and the
 * legend toggles live in StraddleTab; this file only draws. */

import type { ReactElement } from "react";
import { yScale, type Bounds } from "@/components/Reports/analyticsMath";
import type { StraddlePoint } from "@/types/market";
import {
  ChartTipBox,
  H,
  HoverCrosshair,
  W,
  XAxisLabels,
  YAxisLabels,
  YGrid,
  axisLabel,
  hoverIndex,
  niceTicks,
  svgPath,
  useChartHover,
} from "./driftShared";
import { paddedBounds, premiumBounds, type StraddleSeriesKey } from "./straddleMath";

/** One color authority for the tab — the chart lines and the legend
 * chips in StraddleTab both read it. */
export const STRADDLE_COLORS: Record<"straddle" | StraddleSeriesKey, string> = {
  straddle: "var(--accent, #2563eb)",
  spot: "var(--ink-secondary, #6b7280)",
  vwap: "#a16207",
  ce: "#16a34a",
  pe: "#dc2626",
};

export function StraddleChart({
  points,
  visible,
}: Readonly<{
  points: readonly StraddlePoint[];
  visible: ReadonlySet<StraddleSeriesKey>;
}>): ReactElement {
  const premB = premiumBounds(points, visible);
  const spotB = paddedBounds(points.map((p) => p.spot));
  const ticks = niceTicks(premB);
  const hover = useChartHover();
  const hovered = hoverIndex(hover.frac, points.length);
  return (
    <div>
      <ContractReadout points={points} hovered={hovered} />
      <div
        ref={hover.ref}
        className="relative"
        data-testid="straddle-hover-area"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
      >
        <svg
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          preserveAspectRatio="none"
          className="h-48 w-full"
          role="img"
          aria-label="Rolling straddle premium vs spot"
        >
          <YGrid values={ticks} bounds={premB} height={H} />
          <Lines points={points} visible={visible} premB={premB} spotB={spotB} />
          {hovered !== null && <HoverCrosshair i={hovered} n={points.length} />}
        </svg>
        <Axes ticks={ticks} premB={premB} spotB={spotB} showSpot={visible.has("spot")} />
        {hovered !== null && hover.frac !== null && (
          <StraddleTip point={points[hovered]} frac={hover.frac} />
        )}
      </div>
      <XAxisLabels series={points} mode="intraday" />
    </div>
  );
}

/** The ATM pair's live contract readout (StockMock's top-right box):
 * strike+side, LTP, session volume and solved IV — following the cursor,
 * or the session's last minute while not hovering. */
function ContractReadout({
  points,
  hovered,
}: Readonly<{ points: readonly StraddlePoint[]; hovered: number | null }>): ReactElement | null {
  const point = hovered === null ? points.at(-1) : points[hovered];
  if (point === undefined) return null;
  return (
    <div
      data-testid="straddle-readout"
      className="mb-1 flex flex-col items-end gap-0.5 font-mono text-[11px]"
    >
      <ReadoutRow
        label={`${point.atm.toLocaleString("en-IN")} CE`}
        color="ce"
        ltp={point.ce}
        vol={point.ce_vol}
        iv={point.ce_iv}
      />
      <ReadoutRow
        label={`${point.atm.toLocaleString("en-IN")} PE`}
        color="pe"
        ltp={point.pe}
        vol={point.pe_vol}
        iv={point.pe_iv}
      />
    </div>
  );
}

function ReadoutRow({
  label,
  color,
  ltp,
  vol,
  iv,
}: Readonly<{
  label: string;
  color: keyof typeof STRADDLE_COLORS;
  ltp: number;
  vol: number;
  iv: number | null;
}>): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="flex items-center gap-1 text-ink">
        <span
          className="inline-block h-0.5 w-3 rounded-full"
          style={{ backgroundColor: STRADDLE_COLORS[color] }}
        />
        {label}
      </span>
      <span className="text-ink-secondary">
        LTP <span className="text-ink">{fmtPremium(ltp)}</span>
      </span>
      <span className="text-ink-secondary">
        Vol <span className="text-ink">{fmtVol(vol)}</span>
      </span>
      <span className="text-ink-secondary">
        {/* Stored as a fraction (0.112) — shown as percent, the way
            every option chain quotes it. */}
        IV <span className="text-ink">{iv === null ? "—" : (iv * 100).toFixed(1)}</span>
      </span>
    </div>
  );
}

/** Indian-market volume shorthand: 70,200 → "70.2K", 340,000 → "3.4L",
 * 156,548,340 → "15.7Cr" (session totals on NIFTY reach crores). */
export function fmtVol(v: number): string {
  if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(1)}Cr`;
  if (v >= 100_000) return `${(v / 100_000).toFixed(1)}L`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

/** Premium on the RIGHT, spot on the LEFT (StockMock layout). */
function Axes({
  ticks,
  premB,
  spotB,
  showSpot,
}: Readonly<{
  ticks: readonly number[];
  premB: Bounds;
  spotB: Bounds;
  showSpot: boolean;
}>): ReactElement {
  return (
    <>
      <YAxisLabels values={ticks} bounds={premB} height={H} format={fmtPremium} side="right" />
      {showSpot && (
        <YAxisLabels
          values={niceTicks(spotB)}
          bounds={spotB}
          height={H}
          format={fmtSpot}
          side="left"
        />
      )}
    </>
  );
}

function Lines({
  points,
  visible,
  premB,
  spotB,
}: Readonly<{
  points: readonly StraddlePoint[];
  visible: ReadonlySet<StraddleSeriesKey>;
  premB: Bounds;
  spotB: Bounds;
}>): ReactElement {
  const vwap = points.filter((p) => p.vwap !== null);
  return (
    <>
      {visible.has("spot") && (
        <Line values={points.map((p) => p.spot)} bounds={spotB} color="spot" dash="2 3" />
      )}
      {visible.has("ce") && <Line values={points.map((p) => p.ce)} bounds={premB} color="ce" />}
      {visible.has("pe") && <Line values={points.map((p) => p.pe)} bounds={premB} color="pe" />}
      {visible.has("vwap") && vwap.length > 1 && (
        <Line values={vwap.map((p) => p.vwap ?? 0)} bounds={premB} color="vwap" dash="6 4" />
      )}
      <Line values={points.map((p) => p.straddle)} bounds={premB} color="straddle" width={2} />
    </>
  );
}

function Line({
  values,
  bounds,
  color,
  width = 1.5,
  dash,
}: Readonly<{
  values: readonly number[];
  bounds: Bounds;
  color: keyof typeof STRADDLE_COLORS;
  width?: number;
  dash?: string;
}>): ReactElement {
  if (values.length === 1) {
    return (
      <circle cx={4} cy={yScale(values[0] ?? 0, bounds, H)} r={3} fill={STRADDLE_COLORS[color]} />
    );
  }
  return (
    <path
      d={svgPath(values, bounds)}
      fill="none"
      stroke={STRADDLE_COLORS[color]}
      strokeWidth={width}
      {...(dash === undefined ? {} : { strokeDasharray: dash })}
      strokeLinejoin="round"
    />
  );
}

/** The hovered minute's headline numbers. The per-leg CE/PE detail lives
 * in the ContractReadout, which follows the same hover — repeating it
 * here just made the tip tall (operator request 2026-08-10). */
function StraddleTip({
  point,
  frac,
}: Readonly<{ point: StraddlePoint | undefined; frac: number }>): ReactElement | null {
  if (point === undefined) return null;
  return (
    <ChartTipBox frac={frac}>
      <div className="mb-1 text-ink-muted">
        {axisLabel(point.at, "intraday")} · ATM {point.atm.toLocaleString("en-IN")}
      </div>
      <TipRow label="Straddle" color="straddle" value={point.straddle} />
      {point.vwap !== null && <TipRow label="VWAP" color="vwap" value={point.vwap} />}
      <TipRow label="Spot" color="spot" value={point.spot} format={fmtSpot} />
    </ChartTipBox>
  );
}

function TipRow({
  label,
  color,
  value,
  format = fmtPremium,
}: Readonly<{
  label: string;
  color: keyof typeof STRADDLE_COLORS;
  value: number;
  format?: (v: number) => string;
}>): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1 text-ink-muted">
        <span
          className="inline-block h-0.5 w-3 rounded-full"
          style={{ backgroundColor: STRADDLE_COLORS[color] }}
        />
        {label}
      </span>
      <span className="font-mono text-ink">{format(value)}</span>
    </div>
  );
}

export function fmtPremium(v: number): string {
  return v.toLocaleString("en-IN", { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

function fmtSpot(v: number): string {
  return Math.round(v).toLocaleString("en-IN");
}
